import React from "react";
import { useState, useEffect, useRef } from "react";
import { ChevronDown, ChevronRight, ArrowUp } from "lucide-react";
import moment from "moment";

const Division = ({
  divisionGroups,
  divisionName,
  answers,
  handleAnswerChange,
  isExpanded, // expand/collapse state
  onToggleExpand, // toggle function
}) => {
  return (
    <div className="mb-4 border-b pb-2">
      {/* Division header with expand/collapse */}
      <div
        className="flex justify-between items-center cursor-pointer mb-2"
        onClick={onToggleExpand}
      >
        <h2 className="text-lg font-bold text-[#06513D] flex items-center">
          {/* Chevron icons for expand/collapse */}
          {isExpanded ? (
            <ChevronDown
              size={18}
              className="mr-2"
            />
          ) : (
            <ChevronRight
              size={18}
              className="mr-2"
            />
          )}
          {divisionName}
        </h2>
      </div>

      {/* Show content only when expanded */}
      {isExpanded && (
        <div className="pl-4 space-y-4">
          {renderQuestions(divisionGroups, answers, handleAnswerChange)}
        </div>
      )}
    </div>
  );
};

const ChildQuestionCard = ({
  optionLabel,
  optionValue,
  question,
  allQuestions,
  questionNumber,
  answers,
  handleAnswerChange,
  renderQuestionRecursively,
}) => {
  const [isExpanded, setIsExpanded] = useState(false); // Start expanded

  const toggleExpand = () => setIsExpanded(!isExpanded);

  const childQuestions = allQuestions.filter(
    (child) =>
      child.condition &&
      child.condition.questionId === question.questionId &&
      child.condition.value === optionValue
  );

  return (
    <div className="border border-gray-300 rounded-lg  bg-white shadow-sm">
      {/* Card Header - Collapsible */}
      <div
        className={`${
          isExpanded
            ? "bg-[#054030] text-white rounded-t-lg"
            : "border border-[#054030] text-[#054030] rounded-lg"
        }  px-4 py-3  cursor-pointer flex justify-between items-center  transition-colors`}
        onClick={toggleExpand}
      >
        <span className="font-medium">{optionLabel}</span>
        <button
          type="button"
          className="text-xl font-bold"
        >
          {isExpanded ? "-" : "+"}
        </button>
      </div>

      {isExpanded && (
        <div className="p-4">
          {(() => {
            const hasTitle = childQuestions.some((c) => c.type === "title");

            return (
              <div
                className={`grid grid-cols-1 ${
                  hasTitle ? "md:grid-cols-2" : ""
                } gap-2`}
              >
                {childQuestions.map((child, idx) => (
                  <div
                    key={child.questionId}
                    className={
                      child.type === "title"
                        ? "col-span-2 text-center bg-[#06513D] p-2 capitalize text-white rounded-lg"
                        : ""
                    }
                  >
                    {renderQuestionRecursively(
                      child,
                      allQuestions,
                      `${questionNumber}.${idx + 1}`,
                      answers,
                      handleAnswerChange
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

const MultiSelectPicklist = ({
  question,
  allQuestions,
  questionNumber,
  answers,
  handleAnswerChange,
  renderQuestionRecursively,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showGoToTop, setShowGoToTop] = useState(false);
  const selectedValues = answers[question.questionId] || [];
  const dropdownRef = useRef(null);
  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleOptionToggle = (optValue) => {
    handleAnswerChange(question.questionId, optValue, "multi-select");
  };

  // Check scroll position to show/hide Go to Top button
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      setShowGoToTop(scrollTop > 300); // Show button after scrolling 300px
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) return "Select options...";
    if (selectedValues.length === 1) {
      const selectedOption = question.options.find(
        (opt) => opt.value === selectedValues[0]
      );
      return selectedOption ? selectedOption.key : "1 item selected";
    }
    return `${selectedValues.length} items selected`;
  };

  const getSelectedChips = () => {
    return selectedValues.map((value) => {
      const option = question.options.find((opt) => opt.value === value);
      return option ? option.key : value;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // if (question.label === "Other") {
  //   return (
  //     <div className="mt-4 flex gap-6" style={{ border: "1px solid black" }}>
  //       {/* First input field */}
  //       <div className="flex flex-col flex-1" style={{ border: "1px solid blue" }}>
  //         <label className="block text-sm font-medium text-gray-700 mb-2">
  //           Name
  //         </label>
  //         <input
  //           type="text"
  //           placeholder="Please enter name"
  //           value={answers[`${question.questionId}_other_name`] || ""}
  //           onChange={(e) =>
  //             handleAnswerChange(`${question.questionId}_other_name`, e.target.value)
  //           }
  //           className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm h-12"
  //         />
  //       </div>

  //       {/* Second input field */}
  //       <div className="flex flex-col flex-1" style={{ border: "1px solid blue" }}>
  //         <label className="block text-sm font-medium text-gray-700 mb-2">
  //           Value
  //         </label>
  //         <input
  //           type="text"
  //           placeholder="Please enter value"
  //           value={answers[`${question.questionId}_other_value`] || ""}
  //           onChange={(e) =>
  //             handleAnswerChange(`${question.questionId}_other_value`, e.target.value)
  //           }
  //           className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm h-12"
  //         />
  //       </div>
  //     </div>
  //   )
  // }

  // Check if selected answer is "Other"
  // Show extra inputs when "Other" is selected in multi-select

  return (
    <div className="mb-3 mt-4 border rounded-lg shadow p-4">
      <label className="block mb-2 font-medium">
        Q.{questionNumber} {question.label}
      </label>

      <div
        className="relative"
        ref={dropdownRef}
      >
        {/* Dropdown Trigger */}
        <div
          className="border border-gray-300 rounded px-3 py-2 cursor-pointer bg-white flex justify-between items-center min-h-[40px]"
          onClick={toggleDropdown}
        >
          <div className="flex-1">
            {selectedValues.length === 0 ? (
              <span className="text-gray-400">Select options...</span>
            ) : selectedValues.length <= 7 ? (
              <div className="flex flex-wrap gap-1">
                {getSelectedChips().map((chip, index) => (
                  <span
                    key={index}
                    className="bg-[#06513D] text-white px-2 py-1 rounded text-sm"
                  >
                    {chip}
                    <button
                      type="button"
                      className="ml-2 text-white hover:text-gray-200"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOptionToggle(selectedValues[index]);
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-gray-700">
                {selectedValues.length} items selected
              </span>
            )}
          </div>
          <ChevronDown
            size={20}
            className={`transform transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute z-10 w-full  bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
            {question.options.map((opt) => {
              const isSelected = selectedValues.includes(opt.value);
              const hasChildQuestions = allQuestions.some(
                (child) =>
                  child.condition &&
                  child.condition.questionId === question.questionId &&
                  child.condition.value === opt.value
              );

              return (
                <div key={opt._id}>
                  {/* <div className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 border-b"> */}
                  {opt.value === "" ? (
                    <div className="flex items-center justify-between px-3 py-2  border-b bg-[#06513D] ">
                      <span className="text-white font-bold cursor-not-allowed pointer-events-none">
                        {opt.key}
                      </span>
                    </div>
                  ) : (
                    <label className="flex items-center  px-3 py-2  border-b cursor-pointer flex-1">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleOptionToggle(opt.value)}
                        className="mr-2 text-[#06513D] accent-[#06513D]"
                      />
                      <span className="text-gray-700">{opt.key}</span>
                    </label>
                  )}
                  {/* </div> */}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected items summary outside dropdown */}
      {/* {selectedValues.length > 0 && (
        <div className="mt-3 p-2 bg-gray-50 rounded">
          <small className="text-gray-600">
            Selected: {getSelectedChips().join(", ")}
          </small>
        </div>
      )} */}

      {/* Child Question Cards - Outside the dropdown */}
      <div className="mt-4 space-y-3">
        {selectedValues.map((selectedValue) => {
          const selectedOption = question.options.find(
            (opt) => opt.value === selectedValue
          );
          const hasChildQuestions = allQuestions.some(
            (child) =>
              child.condition &&
              child.condition.questionId === question.questionId &&
              child.condition.value === selectedValue

            // child.type !== "title"
          );

          if (!hasChildQuestions) return null;

          return (
            <ChildQuestionCard
              key={selectedValue}
              optionLabel={selectedOption ? selectedOption.key : selectedValue}
              optionValue={selectedValue}
              question={question}
              allQuestions={allQuestions}
              questionNumber={questionNumber}
              answers={answers}
              handleAnswerChange={handleAnswerChange}
              renderQuestionRecursively={renderQuestionRecursively}
            />
          );
        })}
      </div>
    </div>
  );
};

const renderQuestionRecursively = (
  question,
  allQuestions,
  questionNumber,
  answers,
  handleAnswerChange
) => {
  const value = answers[question.questionId];

  let Questionaccordians = null;
  console.log(question, "questions+other");

  if (question.type === "multi-select") {
    return (
      <MultiSelectPicklist
        key={question.questionId}
        question={question}
        allQuestions={allQuestions}
        questionNumber={questionNumber}
        answers={answers}
        handleAnswerChange={handleAnswerChange}
        renderQuestionRecursively={renderQuestionRecursively}
      />
    );
  }

  console.log(allQuestions, "allquestionssss");

  // Filter questions that have "Other" option in their options array
  const tableChildrens = allQuestions.filter(
    (child) =>
      Array.isArray(child.options) &&
      child.options.some((opt) => opt.value === "Other")
  );

  console.log(tableChildrens, "othervalues");

  const tableChildren = allQuestions.filter(
    (child) =>
      child.condition &&
      child.condition.questionId === question.questionId &&
      child.condition.value === "123"
  );

  const shouldShowTable = question.type === "title" && tableChildren.length > 0;

  return (
    <div
      key={question.questionId}
      className={`mt-3 ${
        questionNumber.includes(".")
          ? question.type !== "select"
            ? question.type === "title"
              ? ""
              : "mb-2 mt-4 border rounded-lg shadow p-4"
            : "mb-2 border rounded-lg shadow p-4 pb-7 mt-4 "
          : "mb-2 border rounded-lg shadow p-4"
      }`}
    >
      <label className="block mb-1 font-medium">
        Q.{questionNumber} {question.label}
      </label>

      {Questionaccordians || renderInput(question, answers, handleAnswerChange)}

      {/* Other logic remove*/}
      {/* {Array.isArray(question.options) &&
        question.options.some((opt) => opt.value === "Other") &&
        value === "Other" && (
          <div className="mt-4 flex gap-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
           
            <div className="flex flex-col flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name
              </label>
              <input
                type="text"
                placeholder="Please enter name"
                value={answers[`${question.questionId}_other_name`] || ""}
                onChange={(e) =>
                  handleAnswerChange(`${question.questionId}_other_name`, e.target.value)
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm h-12"
              />
            </div>

          
            <div className="flex flex-col flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Value
              </label>
              <input
                type="text"
                placeholder="Please enter value"
                value={answers[`${question.questionId}_other_value`] || ""}
                onChange={(e) =>
                  handleAnswerChange(`${question.questionId}_other_value`, e.target.value)
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm h-12"
              />
            </div>
          </div>
        )} */}

      {/* Other logic remove*/}
      {/* {question.type === "multi-select" &&
        Array.isArray(question.options) &&
        question.options.some((opt) => opt.value === "Other") &&
        Array.isArray(value) &&
        value.includes("Other") && (
          <div className="mt-4 flex gap-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          
            <div className="flex flex-col flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Other - Name
              </label>
              <input
                type="text"
                placeholder="Please enter name"
                value={answers[`${question.questionId}_other_name`] || ""}
                onChange={(e) =>
                  handleAnswerChange(`${question.questionId}_other_name`, e.target.value)
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm h-12"
              />
            </div>

           
            <div className="flex flex-col flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Other - Value
              </label>
              <input
                type="text"
                placeholder="Please enter value"
                value={answers[`${question.questionId}_other_value`] || ""}
                onChange={(e) =>
                  handleAnswerChange(`${question.questionId}_other_value`, e.target.value)
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm h-12"
              />
            </div>
          </div>
        )} */}

      {shouldShowTable && (
        <div className="mt-4 overflow-x-auto bg-gray-50 p-3 rounded">
          <table className="table-auto border-collapse w-full">
            <tbody>
              {Array.from({ length: Math.ceil(tableChildren.length / 2) }).map(
                (_, rowIndex) => {
                  const row = tableChildren.slice(
                    rowIndex * 2,
                    rowIndex * 2 + 2
                  );

                  return (
                    <React.Fragment key={rowIndex}>
                      {/* ---- Title Rows ---- */}
                      {row.map((child) => {
                        if (child.type === "title") {
                          return (
                            <tr
                              key={child.questionId}
                              className="bg-white"
                            >
                              <th
                                colSpan={2}
                                className="border px-4 py-2 text-center text-[#06513D] font-light"
                              >
                                {child.label}
                              </th>
                            </tr>
                          );
                        }
                      })}

                      {/* ---- Skip rendering labels/inputs for title-only rows ---- */}
                      {row.some((c) => c.type !== "title") && (
                        <>
                          {/* ---- Label Row ---- */}
                          <tr className="bg-[#E6F2EF]">
                            {row
                              .filter((c) => c.type !== "title")
                              .map((c) => (
                                <th
                                  key={c.questionId}
                                  className="border px-4 py-2 text-center text-[#06513D] font-light"
                                >
                                  {c.label}
                                </th>
                              ))}

                            {/* Fill empty cell if odd number */}
                            {row.filter((c) => c.type !== "title").length ===
                              1 && <th className="border px-4 py-2"></th>}
                          </tr>

                          {/* ---- Input Row ---- */}
                          <tr>
                            {row
                              .filter((c) => c.type !== "title")
                              .map((c) => (
                                <td
                                  key={c.questionId}
                                  className="border px-4 py-2"
                                >
                                  {renderInput(c, answers, handleAnswerChange)}

                                  {/* ---- Other Option Handling ---- */}
                                  {Array.isArray(c.options) &&
                                    c.options.some(
                                      (opt) => opt.value === "Other"
                                    ) &&
                                    answers[c.questionId] === "Other" && (
                                      <div className="mt-1 gap-2 flex w-40">
                                        <input
                                          type="text"
                                          placeholder="Enter name"
                                          value={
                                            answers[
                                              `${c.questionId}_other_name`
                                            ] || ""
                                          }
                                          onChange={(e) =>
                                            handleAnswerChange(
                                              `${c.questionId}_other_name`,
                                              e.target.value
                                            )
                                          }
                                          className="w-36 px-3 py-1 border border-gray-300 rounded text-xs"
                                        />

                                        <input
                                          type="text"
                                          placeholder="Enter value"
                                          value={
                                            answers[
                                              `${c.questionId}_other_value`
                                            ] || ""
                                          }
                                          onChange={(e) =>
                                            handleAnswerChange(
                                              `${c.questionId}_other_value`,
                                              e.target.value
                                            )
                                          }
                                          className="w-36 px-3 py-1 border border-gray-300 rounded text-xs"
                                        />
                                      </div>
                                    )}
                                </td>
                              ))}

                            {/* Empty cell for odd count */}
                            {row.filter((c) => c.type !== "title").length ===
                              1 && <td className="border px-4 py-2"></td>}
                          </tr>
                        </>
                      )}
                    </React.Fragment>
                  );
                }
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Render child questions - This is where the "Other" question should be processed */}
      {allQuestions
        .filter(
          (child) =>
            child.condition &&
            child.condition.questionId === question.questionId &&
            (question.type === "multi-select"
              ? Array.isArray(value) && value.includes(child.condition.value)
              : value === child.condition.value)
        )
        .map((child, idx) => {
          console.log(
            "Rendering child question:",
            child,
            "with parent value:",
            value
          );
          return renderQuestionRecursively(
            child,
            allQuestions,
            `${questionNumber}.${idx + 1}`,
            answers,
            handleAnswerChange
          );
        })}
    </div>
  );
};

const renderQuestions = (divisionGroups, answers, handleAnswerChange) => {
  let parentCounter = 0;

  return divisionGroups.map((group) => {
    const rootQuestions = group.questions.filter((q) => !q.condition);

    return rootQuestions.map((question) => {
      parentCounter++;
      const questionNumber = parentCounter;

      return renderQuestionRecursively(
        question,
        group.questions,
        questionNumber.toString(),
        answers,
        handleAnswerChange
      );
    });
  });
};

const renderInput = (question, answers, handleAnswerChange) => {
  console.log("question -->", question);

  console.log(question, "questiontype");
  // other logic removed
  // if (question.label === "Other") {
  //   return (
  //     <div className="mt-4 flex gap-6" >
  //       {/* First input field */}
  //       <div className="flex flex-col flex-1" >
  //         <label className="block text-sm font-medium text-gray-700 mb-2">
  //           Name
  //         </label>
  //         <input
  //           type="text"
  //           placeholder="Please enter name"
  //           value={answers[`${question.questionId}_other_name`] || ""}
  //           onChange={(e) =>
  //             handleAnswerChange(`${question.questionId}_other_name`, e.target.value)
  //           }
  //           className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm h-12"
  //         />
  //       </div>

  //       {/* Second input field */}
  //       <div className="flex flex-col flex-1" >
  //         <label className="block text-sm font-medium text-gray-700 mb-2">
  //           Value
  //         </label>
  //         <input
  //           type="text"
  //           placeholder="Please enter value"
  //           value={answers[`${question.questionId}_other_value`] || ""}
  //           onChange={(e) =>
  //             handleAnswerChange(`${question.questionId}_other_value`, e.target.value)
  //           }
  //           className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm h-12"
  //         />
  //       </div>
  //     </div>
  //   )
  // }

  if (question.type === "text") {
    return (
      <textarea
        type="text"
        className="border p-2 w-full"
        placeholder="Please Enter the text"
        style={{
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
          minHeight: "10px",
          height: "auto",
        }}
        value={answers[question.questionId] || ""}
        onChange={(e) =>
          handleAnswerChange(question.questionId, e.target.value)
        }
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            const currentValue = e.target.value;
            const cursorPos = e.target.selectionStart;
            const newValue =
              currentValue.slice(0, cursorPos) +
              "\n" +
              currentValue.slice(cursorPos);
            handleAnswerChange(question.questionId, newValue);

            // Set cursor position after the new line
            setTimeout(() => {
              e.target.setSelectionRange(cursorPos + 1, cursorPos + 1);
            }, 0);
          }
        }}
      />
    );
  }

  if (question.type === "number") {
    return (
      <input
        type="number"
        className="border p-2 w-full min-h-10 mb-3 pb-3"
        placeholder="Please enter the number"
        value={answers[question.questionId] || ""}
        onChange={(e) =>
          handleAnswerChange(question.questionId, e.target.value)
        }
      />
    );
  }

  // Contact number
  if (question.type === "contact_number") {
    return (
      <input
        type="number"
        className="border p-2 w-full min-h-10 mb-3 pb-3"
        placeholder="Please enter the contact number"
        maxLength={10}
        value={answers[question.questionId] || ""}
        onChange={(e) =>
          handleAnswerChange(question.questionId, e.target.value)
        }
      />
    );
  }
  // Email
  if (question.type === "mail") {
    return (
      <input
        type="email"
        className="border p-2 w-full min-h-10 mb-3 pb-3"
        placeholder="Please enter the email"
        value={answers[question.questionId] || ""}
        onChange={(e) =>
          handleAnswerChange(question.questionId, e.target.value)
        }
      />
    );
  }

  // Date
  if (question.type === "date") {
    const storedValue = answers[question.questionId];

    return (
      <input
        type="date"
        className="border rounded p-2 w-60"
        value={answers[question.questionId] || ""}
        onChange={(e) =>
          handleAnswerChange(question.questionId, e.target.value)
        }
      />
    );
  }

  // percentage handled
  if (question.type === "percentage") {
    return (
      <div
        key={question.questionId}
        className="relative  pb-2 mt-2"
      >
        <input
          type="number"
          min="0"
          max="100"
          className="border p-2 w-full h-10 pr-8"
          placeholder="Enter percentage"
          value={answers[question.questionId] || ""}
          onChange={(e) => {
            let value = Number(e.target.value);
            if (value > 100) value = 100;
            if (value < 0) value = 0;
            handleAnswerChange(question.questionId, value);
          }}
        />
        <span className="absolute right-3 top-1/2 -translate-y-3 text-gray-500">
          %
        </span>
      </div>
    );
  }

  if (question.type === "file") {
    return (
      <input
        type="file"
        className="border p-2 w-full"
        placeholder="Please upload file"
        onChange={(e) =>
          handleAnswerChange(question.questionId, e.target.files[0])
        }
      />
    );
  }

  if (question.type === "select") {
    return (
      <select
        className="border p-2  h-10 w-full "
        value={answers[question.questionId] || ""}
        onChange={(e) =>
          handleAnswerChange(question.questionId, e.target.value)
        }
      >
        <option value="">Select</option>
        {question.options.map((opt) => (
          <option
            key={opt.key}
            value={opt.value}
            className={
              !opt.value
                ? "bg-[#06513D] text-white font-bold cursor-not-allowed pointer-events-none"
                : ""
            }
            disabled={!opt.value}
          >
            {opt.value || opt.key}
          </option>
        ))}
      </select>
    );
  }

  if (question.type === "multi-select") {
    return (
      <div className="space-y-2">
        {question.options.map((opt) => {
          const isChecked = (answers[question.questionId] || []).includes(
            opt.value
          );
          return (
            <div
              key={opt._id}
              className="flex items-center space-x-2 "
            >
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() =>
                  handleAnswerChange(
                    question.questionId,
                    opt.value,
                    "multi-select"
                  )
                }
              />
              <span>{opt.key}</span>
            </div>
          );
        })}
      </div>
    );
  }

  // return null;
};

export const QuestionForm = ({ questions, expandAll }) => {
  // Expand/collapse states
  const [expandedIds, setExpandedIds] = useState([]);
  const [expandedDivisions, setExpandedDivisions] = useState({});
  const [answers, setAnswers] = useState({});

  console.log(questions, "questionsownership");

  // Initialize divisions as open by default
  useEffect(() => {
    const groupedDivisions = questions.reduce((acc, curr) => {
      const division = curr.division || "Untitled Division";
      if (!acc[division]) acc[division] = [];
      acc[division].push(curr);
      return acc;
    }, {});

    const initialExpanded = {};
    Object.keys(groupedDivisions).forEach((division) => {
      initialExpanded[division] = true;
    });
    setExpandedDivisions(initialExpanded);
  }, [questions]);

  // Handle expand all functionality
  useEffect(() => {
    if (expandAll !== undefined && questions.length > 0) {
      const groupedDivisions = questions.reduce((acc, curr) => {
        const division = curr.division || "Untitled Division";
        if (!acc[division]) acc[division] = [];
        acc[division].push(curr);
        return acc;
      }, {});

      const allExpanded = {};
      Object.keys(groupedDivisions).forEach((division) => {
        allExpanded[division] = expandAll;
      });
      setExpandedDivisions(allExpanded);

      if (expandAll) {
        const allIds = questions.map((group) => group.id);
        setExpandedIds(allIds);
      } else {
        setExpandedIds([]);
      }
    }
  }, [expandAll, questions]);

  // Toggle individual question groups
  const toggleExpand = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  // Toggle individual divisions
  const toggleDivisionExpand = (divisionName) => {
    setExpandedDivisions((prev) => ({
      ...prev,
      [divisionName]: !prev[divisionName],
    }));
  };

  const handleAnswerChange = (questionId, value, type = "text") => {
    setAnswers((prev) => {
      if (type === "multi-select") {
        const prevArray = prev[questionId] || [];
        const newArray = prevArray.includes(value)
          ? prevArray.filter((v) => v !== value)
          : [...prevArray, value];
        return { ...prev, [questionId]: newArray };
      }
      return { ...prev, [questionId]: value };
    });
  };

  const groupedDivisions = questions.reduce((acc, curr) => {
    const division = curr.division || "Untitled Division";
    if (!acc[division]) acc[division] = [];
    acc[division].push(curr);
    return acc;
  }, {});

  return (
    <div className="max-w-4xl mx-auto p-6 font-inter">
      <p className="text-black"> hsoia</p>
      <form>
        {Object.entries(groupedDivisions).map(
          ([divisionName, divisionGroups]) => (
            <Division
              key={divisionName}
              divisionGroups={divisionGroups}
              divisionName={divisionName}
              answers={answers}
              handleAnswerChange={handleAnswerChange}
              isExpanded={expandedDivisions[divisionName]} // pass expand state
              onToggleExpand={() => toggleDivisionExpand(divisionName)} // pass toggle function
            />
          )
        )}
      </form>
    </div>
  );
};
